import { Component, signal, inject } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

import { ChatComponent } from './chat.component';
import { ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [ChatComponent],
  animations: [
    trigger('panelAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px) scale(0.98)' }),
        animate('200ms cubic-bezier(0.2, 0, 0, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' })
        ),
      ]),
      transition(':leave', [
        animate('160ms cubic-bezier(0.4, 0, 1, 1)',
          style({ opacity: 0, transform: 'translateY(12px) scale(0.98)' })
        ),
      ]),
    ])
  ],
  template: `
    @if (showLauncher() && !open()) {
      <button class="askomega-btn"
        (click)="toggleOpen()"
        aria-label="Open chat"
        >
        <span class="askomega-btn-ring"></span>
        <span class="askomega-btn-mark">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.5c.4 0 .74.27.84.66l.9 3.5a4.6 4.6 0 003.3 3.3l3.5.9a.87.87 0 010 1.68l-3.5.9a4.6 4.6 0 00-3.3 3.3l-.9 3.5a.87.87 0 01-1.68 0l-.9-3.5a4.6 4.6 0 00-3.3-3.3l-3.5-.9a.87.87 0 010-1.68l3.5-.9a4.6 4.6 0 003.3-3.3l.9-3.5c.1-.39.44-.66.84-.66z"/>
          </svg>
        </span>
        <span class="askomega-btn-label">Ask Civie-AI</span>
      </button>
    }
    
    @if (open()) {
      <div class="chat-window-sction"
        @panelAnim
        role="dialog" aria-label="Chat dialog"
        >
        <div class="widget-header">
          <div class="widget-header-identity">
            <div class="agent-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
                <path d="M12 2.5c.4 0 .74.27.84.66l.9 3.5a4.6 4.6 0 003.3 3.3l3.5.9a.87.87 0 010 1.68l-3.5.9a4.6 4.6 0 00-3.3 3.3l-.9 3.5a.87.87 0 01-1.68 0l-.9-3.5a4.6 4.6 0 00-3.3-3.3l-3.5-.9a.87.87 0 010-1.68l3.5-.9a4.6 4.6 0 003.3-3.3l.9-3.5c.1-.39.44-.66.84-.66z"/>
              </svg>
            </div>
            <div class="widget-header-text">
              <div class="widget-header-title">{{ title() }}</div>
            </div>
          </div>
          <div class="widget-header-actions">
            <button (click)="startNewChat()" aria-label="New chat" title="Start new chat" class="widget-header-btn">
              <i class="bi bi-plus-circle"></i>
            </button>
            <button (click)="copyAll()" aria-label="Copy entire chat" title="Copy chat" class="widget-header-btn">
              @if (!copiedAll()) {
                <i class="bi bi-copy"></i>
              }
              @if (copiedAll()) {
                <i class="bi bi-check2"></i>
              }
            </button>
            <button (click)="toggleOpen()" aria-label="Close chat" title="Close" class="widget-header-btn widget-header-btn-close"><i class="bi bi-x" style="font-size: 21px"></i></button>
          </div>
        </div>
        <div style="flex: 1; min-height: 0; display: flex;">
          <app-chat #chat style="display: block; height: 100%; width: 100%;"></app-chat>
        </div>
      </div>
    }
    `,
})
export class ChatWidgetComponent {
  private route = inject(ActivatedRoute);
  @ViewChild('chat') chatComponent?: ChatComponent;

  open = signal<boolean>(false);
  showLauncher = signal<boolean>(true);
  title = signal<string>('Civie-AI');
  copiedAll = signal<boolean>(false);

  constructor() {
    const qp = this.route.snapshot.queryParamMap;
    const openParam = qp.get('open');
    const launcherParam = qp.get('launcher');
    const titleParam = qp.get('title');

    if (openParam && ['1', 'true', 'yes'].includes(openParam.toLowerCase())) {
      this.open.set(true);
    }
    if (launcherParam && ['0', 'false', 'no'].includes(launcherParam.toLowerCase())) {
      this.showLauncher.set(false);
      // If launcher is hidden and open not specified, open by default
      if (!openParam) this.open.set(true);
    }
    if (titleParam) {
      this.title.set(titleParam);
    }

    // Notify parent on initial load about current desired size
    this.notifyParentSize();
  }
  toggleOpen(): void {
    this.open.set(!this.open());
    // Notify parent to resize iframe when state changes
    this.notifyParentSize();
  }

  startNewChat(): void {
    this.chatComponent?.resetChat();
  }

  async copyAll(): Promise<void> {
    try {
      const text = this.chatComponent?.getTranscriptText() ?? '';
      if (!text) return;
      if ((navigator as any)?.clipboard?.writeText) {
        await (navigator as any).clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      this.copiedAll.set(true);
      setTimeout(() => this.copiedAll.set(false), 1500);
    } catch (e) {
      console.error('Copy all failed:', e);
    }
  }

  private notifyParentSize(): void {
    try {
      const desired = this.open()
        ? { width: 768, height: 688 }
        : { width: 180, height: 92 };
      const message = {
        source: 'omega-chat-widget',
        type: 'state',
        open: this.open(),
        launcher: this.showLauncher(),
        size: desired,
        title: this.title(),
      };
      // Cross-origin safe; parent can filter by source/type
      window.parent?.postMessage(message, '*');
    } catch {
      // ignore
    }
  }
}


